import { flattenAllottees, type FlattenedAllotteeRow } from './flattenAllottees';
import { buildSingleAllotteeSignerJson } from './allotmentSigners';
import {
  resolveApplicantIdLabel,
  type IdTypeCodeOption,
} from './idTypeLabelResolve';

export { resolveApplicantIdLabel, type IdTypeCodeOption };

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function buildAddressFromFormData(formData: Record<string, any>): string {
  const postalCode = trim(formData.postalCode);
  const blockNo = trim(formData.blockNo);
  const streetName = trim(formData.streetName);
  const addrLevel = trim(formData.addrLevel);
  const addrUnit = trim(formData.addrUnit);
  const buildingName = trim(formData.buildingName);
  const line1 = [blockNo, streetName].filter(Boolean).join(' ');
  const line2 = [addrLevel, addrUnit].filter(Boolean).join('-');
  const unit = line2 ? `#${line2}` : '';
  return [line1, unit, buildingName, postalCode ? `SINGAPORE ${postalCode}` : ''].filter(Boolean).join(', ').trim();
}

function resolveCompanyAddress(formData: Record<string, any>): string {
  const direct = pickValue(formData, ['companyAddress', 'newOfficeAddress', 'company_address']);
  if (direct) return direct;
  return buildAddressFromFormData(formData);
}

function pickValue(source: Record<string, any>, keys: string[]): string {
  for (const key of keys) {
    const parts = key.split('.');
    let cur: any = source;
    for (const p of parts) cur = cur?.[p];
    const v = trim(cur?.value ?? cur);
    if (v) return v;
  }
  return '';
}

export function buildAllotmentTaskFormData(
  formData: Record<string, any>,
  entityCd: string,
): Record<string, any> {
  const out = { ...(formData || {}) };
  const ec = trim(entityCd);
  if (ec) {
    out.entity = out.entity || ec;
    out.entityCd = out.entityCd || ec;
  }
  return out;
}

export function buildAllotmentEfilingSeedPayload(input: {
  eFilingDataValidation: Record<string, any> | null | undefined;
  eFilingDataForApi: Record<string, any> | null | undefined;
}) {
  return {
    eFilingDataValidation: input.eFilingDataValidation || null,
    eFilingDataForApi: input.eFilingDataForApi || null,
  };
}

export function toShareApplicationVariables(input: {
  formData: Record<string, any>;
  row: FlattenedAllotteeRow;
  applicationDate: string;
  idTypeOptions?: IdTypeCodeOption[];
}): Record<string, any> {
  const { formData, row } = input;
  const companyName = pickValue(formData, ['entityName', 'companyName', 'entityName.value']);
  const companyAddress = resolveCompanyAddress(formData);
  const applicantIdLabel = resolveApplicantIdLabel(row, input.idTypeOptions || []);
  const applicantId = row.idNo;
  const isCorporate = trim(row.categoryCd) === 'B09001';
  const signerName = isCorporate
    ? trim(row.representativeName) || row.allotteeName
    : row.allotteeName;
  const signersJson = buildSingleAllotteeSignerJson({
    categoryCd: row.categoryCd,
    allotteeName: row.allotteeName,
    representativeName: row.representativeName,
  });

  return {
    application_date: trim(input.applicationDate),
    company_name: companyName,
    company_address: companyAddress,
    applicant_pronoun: isCorporate ? 'its' : 'his/her',
    applicant_name_label: isCorporate ? 'Company Name' : 'Applicant Name',
    applicant_name: row.allotteeName,
    applicant_id_label: applicantIdLabel,
    applicant_id: applicantId,
    applicant_address: row.applicantAddress,
    no_of_shares: row.numberOfShares,
    share_class: row.className || row.classCode,
    currency: row.currencyName || row.currencyCode,
    amount_payable: row.paidUpShareCapital,
    signatory_name: signerName,
    corporate: isCorporate,
    signers: signersJson,
  };
}

export function toDirectorsResolutionVariables(input: {
  formData: Record<string, any>;
  rows: FlattenedAllotteeRow[];
  shareholderApprovalDate: string;
  directorsSignersJson: string;
}): Record<string, any> {
  const { formData, rows } = input;
  const companyName = pickValue(formData, ['entityName', 'companyName', 'entityName.value']);
  const uen = pickValue(formData, ['entityRegNo', 'companyRegistrationNo', 'entityRegNo.value']);
  return {
    company_name: companyName,
    uen,
    shareholder_approval_date: trim(input.shareholderApprovalDate),
    allottee: rows.map((r) => ({
      allottee_name: r.allotteeName,
      allottee_class: r.className || r.classCode,
      allottee_shares: r.numberOfShares,
      allottee_currency: r.currencyName || r.currencyCode,
      allottee_amount: r.paidUpShareCapital,
      allottee_consideration: r.paidUpShareCapital,
      allottee_cert_no: r.certificateNo,
    })),
    signers: input.directorsSignersJson,
  };
}

export function toMembersResolutionVariables(input: {
  formData: Record<string, any>;
  membersSignersJson: string;
}): Record<string, any> {
  const companyName = pickValue(input.formData, ['entityName', 'companyName', 'entityName.value']);
  const uen = pickValue(input.formData, ['entityRegNo', 'companyRegistrationNo', 'entityRegNo.value']);
  return {
    company_name: companyName,
    uen,
    signers: input.membersSignersJson,
  };
}

export function buildAllotmentRows(input: {
  formData: Record<string, any>;
  eFilingDataValidation?: Record<string, any> | null;
}): FlattenedAllotteeRow[] {
  return flattenAllottees({
    formData: input.formData || {},
    eFilingDataValidation: input.eFilingDataValidation || undefined,
  });
}

